
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import TLDRWidget from "./TLDRWidget";
import SentimentWidget from "./SentimentWidget";
import { SentimentAnalysis, TLDRAnalysis } from "@/hooks/useArticleAI";
import T from "@/i18n/T";

interface FloatingAIButtonProps {
  tldrData: TLDRAnalysis | null;
  sentimentData: SentimentAnalysis | null;
  loadingTldr: boolean;
  loadingSentiment: boolean;
}

const FloatingAIButton = ({ tldrData, sentimentData, loadingTldr, loadingSentiment }: FloatingAIButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  if (!isMobile) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-36 right-6 z-50"
          >
            <Button
              onClick={() => setIsOpen(true)}
              size="lg"
              className="rounded-full w-14 h-14 shadow-lg bg-byteline-green hover:bg-byteline-green/90"
            >
              <Sparkles className="h-6 w-6" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="flex items-center justify-between">
            <DrawerTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-byteline-green" />
              <T k="auto.ai-article-analysis-d396" />
            </DrawerTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DrawerHeader>
          
          <div className="px-4 pb-6 space-y-4 overflow-y-auto">
            <TLDRWidget
              data={tldrData}
              loading={loadingTldr}
            />
            
            <SentimentWidget
              data={sentimentData}
              loading={loadingSentiment}
            />
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default FloatingAIButton;
